/**
 * Layout configuration for Poker Reborn
 *
 * Data extracted from legacy AS3 clients.
 * See LEGACY_DATA_USAGE.md for source tracking.
 */

import type {SeatPosition, CardOffsets, Point, Size} from '@/types';

// =============================================================================
// DESIGN RESOLUTION
// =============================================================================

/** Base design resolution */
export const DESIGN_RESOLUTION: Size = {
    width: 1920,
    height: 1080,
};

/** Aspect ratio */
export const ASPECT_RATIO = DESIGN_RESOLUTION.width / DESIGN_RESOLUTION.height;

// =============================================================================
// TABLE POSITION
// =============================================================================

/**
 * Table center offset (normalized).
 * Values are relative to screen center (0.5, 0.5).
 * - { x: 0.50, y: 0.50 } = no offset, perfectly centered (default)
 * - { x: 0.50, y: 0.48 } = centered horizontally, shifted up 2%
 */
export const TABLE_POSITION: Point = {x: 0.50, y: 0.43};

/** Table info label position (game type + blinds on felt) */
export const TABLE_INFO_POSITION: Point = {x: 0.50, y: 0.25};

// =============================================================================
// SEAT POSITIONS
// Normalized coordinates (0-1) for responsive scaling
// =============================================================================

/** 2-max (heads up) seat positions */
const SEATS_2MAX: SeatPosition[] = [
    {seat: 0, x: 0.50, y: 0.77},  // Hero (bottom center)
    {seat: 1, x: 0.50, y: 0.09},  // Villain (top center)
];

/** 5-max seat positions */
const SEATS_5MAX: SeatPosition[] = [
    {seat: 0, x: 0.50, y: 0.79},  // Hero (bottom center)
    {seat: 1, x: 0.15, y: 0.59},  // Left bottom
    {seat: 2, x: 0.15, y: 0.22},  // Left top
    {seat: 3, x: 0.50, y: 0.05},  // Top center
    {seat: 4, x: 0.85, y: 0.40},  // Right middle
];

/** 6-max seat positions - aligned closer to table */
const SEATS_6MAX: SeatPosition[] = [
    {seat: 0, x: 0.50, y: 0.77},  // Hero (bottom center)
    {seat: 1, x: 0.15, y: 0.65},  // Left bottom
    {seat: 2, x: 0.15, y: 0.21},  // Left top
    {seat: 3, x: 0.50, y: 0.09},  // Top center
    {seat: 4, x: 0.85, y: 0.21},  // Right top
    {seat: 5, x: 0.85, y: 0.65},  // Right bottom
];

/** 9-max seat positions */
const SEATS_9MAX: SeatPosition[] = [
    {seat: 0, x: 0.50, y: 0.77},  // Hero (bottom center)
    {seat: 1, x: 0.20, y: 0.69},  // Bottom left
    {seat: 2, x: 0.07, y: 0.46},  // Left middle
    {seat: 3, x: 0.15, y: 0.21},  // Left top
    {seat: 4, x: 0.34, y: 0.11},  // Top left
    {seat: 5, x: 0.66, y: 0.11},  // Top right
    {seat: 6, x: 0.85, y: 0.21},  // Right top
    {seat: 7, x: 0.93, y: 0.46},  // Right middle
    {seat: 8, x: 0.80, y: 0.69},  // Bottom right
];

/** All seat positions by table size */
export const SEAT_POSITIONS: Record<2 | 5 | 6 | 9, SeatPosition[]> = {
    2: SEATS_2MAX,
    5: SEATS_5MAX,
    6: SEATS_6MAX,
    9: SEATS_9MAX,
};

// =============================================================================
// BET POSITIONS (chips in front of player)
// Absolute normalized coordinates (0-1) for each seat
// =============================================================================

/** Bet/chip position for each seat (normalized absolute coordinates) */
export const BET_POSITION: Record<2 | 5 | 6 | 9, Point[]> = {
    2: [
        {x: 0.50, y: 0.65},  // Seat 0 (bottom)
        {x: 0.50, y: 0.21},  // Seat 1 (top)
    ],
    5: [
        {x: 0.50, y: 0.69},  // Seat 0 (bottom center)
        {x: 0.21, y: 0.53},  // Seat 1 (left bottom)
        {x: 0.21, y: 0.28},  // Seat 2 (left top)
        {x: 0.50, y: 0.13},  // Seat 3 (top center)
        {x: 0.79, y: 0.40},  // Seat 4 (right middle)
    ],
    6: [
        {x: 0.5, y: 0.60},  // Seat 0
        {x: 0.24, y: 0.54},  // Seat 1
        {x: 0.25, y: 0.30},  // Seat 2
        {x: 0.51, y: 0.20},  // Seat 3
        {x: 0.77, y: 0.28},  // Seat 4
        {x: 0.77, y: 0.56},  // Seat 5
    ],
    9: [
        {x: 0.50, y: 0.67},  // Seat 0 (bottom center)
        {x: 0.24, y: 0.61},  // Seat 1 (bottom left)
        {x: 0.13, y: 0.42},  // Seat 2 (left middle)
        {x: 0.21, y: 0.27},  // Seat 3 (left top)
        {x: 0.38, y: 0.19},  // Seat 4 (top left)
        {x: 0.62, y: 0.19},  // Seat 5 (top right)
        {x: 0.79, y: 0.27},  // Seat 6 (right top)
        {x: 0.87, y: 0.42},  // Seat 7 (right middle)
        {x: 0.76, y: 0.61},  // Seat 8 (bottom right)
    ],
};

/** Player pot position (alias for BET_POSITION) */
export const PLAYER_POT_POSITION = BET_POSITION;

// =============================================================================
// DEALER BUTTON POSITIONS
// Absolute normalized coordinates (0-1) for each seat
// =============================================================================

/** Dealer button position when button is at each seat (normalized global coordinates) */
export const DEALER_BUTTON_POSITION: Record<2 | 5 | 6 | 9, Point[]> = {
    2: [
        {x: 0.50, y: 0.69},   // BTN at seat 0 (bottom)
        {x: 0.50, y: 0.17},   // BTN at seat 1 (top)
    ],
    5: [
        {x: 0.50, y: 0.71},   // BTN at seat 0 (bottom center)
        {x: 0.21, y: 0.55},   // BTN at seat 1 (left bottom)
        {x: 0.21, y: 0.26},   // BTN at seat 2 (left top)
        {x: 0.50, y: 0.13},   // BTN at seat 3 (top center)
        {x: 0.79, y: 0.40},   // BTN at seat 4 (right middle)
    ],
    6: [
        {x: 0.57, y: 0.62},   // BTN at seat 0 (bottom center)
        {x: 0.27, y: 0.61},   // BTN at seat 1 (left bottom)
        {x: 0.22, y: 0.32},   // BTN at seat 2 (left top)
        {x: 0.43, y: 0.21},   // BTN at seat 3 (top center)
        {x: 0.72, y: 0.27},   // BTN at seat 4 (right top)
        {x: 0.72, y: 0.59},   // BTN at seat 5 (right bottom)
    ],
    9: [
        {x: 0.50, y: 0.69},   // BTN at seat 0 (bottom center)
        {x: 0.24, y: 0.63},   // BTN at seat 1 (bottom left)
        {x: 0.13, y: 0.46},   // BTN at seat 2 (left middle)
        {x: 0.21, y: 0.27},   // BTN at seat 3 (left top)
        {x: 0.38, y: 0.19},   // BTN at seat 4 (top left)
        {x: 0.62, y: 0.19},   // BTN at seat 5 (top right)
        {x: 0.79, y: 0.27},   // BTN at seat 6 (right top)
        {x: 0.87, y: 0.46},   // BTN at seat 7 (right middle)
        {x: 0.76, y: 0.63},   // BTN at seat 8 (bottom right)
    ],
};

// =============================================================================
// COMMUNITY CARDS
// =============================================================================

/** Community cards area */
export const COMMUNITY_CARDS = {
    /** Center position (normalized) */
    center: {x: 0.50, y: 0.37},

    /** Spacing between cards (normalized) */
    spacing: 0.055,

    /** Card positions relative to center */
    positions: [
        {x: -0.11, y: 0},   // Card 1 (flop)
        {x: -0.055, y: 0},  // Card 2 (flop)
        {x: 0, y: 0},       // Card 3 (flop)
        {x: 0.055, y: 0},   // Card 4 (turn)
        {x: 0.11, y: 0},    // Card 5 (river)
    ],
};

// =============================================================================
// DECK POSITION (for deal animations)
// =============================================================================

/** Deck position - where cards are dealt from (normalized) */
export const DECK_POSITION: Point = {x: 0.50, y: 0.32};

// =============================================================================
// POT POSITION
// =============================================================================

/** Main pot position (normalized) */
export const POT_POSITION: Point = {x: 0.50, y: 0.52};

/** Side pot offset (normalized, alternates left/right) */
export function getSidePotOffset(potIndex: number): Point {
    if (potIndex === 0) return {x: 0, y: 0};
    const side = potIndex % 2 === 0 ? -1 : 1;
    const distance = Math.ceil(potIndex / 2) * 0.08;
    return {x: side * distance, y: 0};
}

// =============================================================================
// HOLE CARDS POSITIONS
// Absolute normalized coordinates (0-1) for each seat
// =============================================================================

/** Hole cards position for each seat (normalized absolute coordinates) */
export const HOLE_CARDS_POSITION: Record<2 | 5 | 6 | 9, Point[]> = {
    2: [
        {x: 0.50, y: 0.69},  // Seat 0 (bottom)
        {x: 0.50, y: 0.17},  // Seat 1 (top)
    ],
    5: [
        {x: 0.50, y: 0.71},  // Seat 0 (bottom center)
        {x: 0.21, y: 0.55},  // Seat 1 (left bottom)
        {x: 0.21, y: 0.26},  // Seat 2 (left top)
        {x: 0.50, y: 0.13},  // Seat 3 (top center)
        {x: 0.79, y: 0.40},  // Seat 4 (right middle)
    ],
    6: [
        {x: 0.5, y: 0.69},  // Seat 0
        {x: 0.2, y: 0.60},  // Seat 1
        {x: 0.2, y: 0.24},  // Seat 2
        {x: 0.51, y: 0.13},  // Seat 3
        {x: 0.77, y: 0.22},  // Seat 4
        {x: 0.78, y: 0.63},  // Seat 5
    ],
    9: [
        {x: 0.50, y: 0.69},  // Seat 0 (bottom center)
        {x: 0.24, y: 0.63},  // Seat 1 (bottom left)
        {x: 0.13, y: 0.46},  // Seat 2 (left middle)
        {x: 0.21, y: 0.27},  // Seat 3 (left top)
        {x: 0.34, y: 0.19},  // Seat 4 (top left)
        {x: 0.66, y: 0.19},  // Seat 5 (top right)
        {x: 0.79, y: 0.27},  // Seat 6 (right top)
        {x: 0.87, y: 0.46},  // Seat 7 (right middle)
        {x: 0.76, y: 0.63},  // Seat 8 (bottom right)
    ],
};

// =============================================================================
// CARD DIMENSIONS
// =============================================================================

/** Card sizes (at 1920x1080 base) */
export const CARD_SIZES = {
    /** Big cards (community) */
    big: {width: 100, height: 140},

    /** Small cards (hole cards) */
    small: {width: 70, height: 98},

    /** Corner radius */
    cornerRadius: {big: 10, small: 7},
};

/** Hole card offsets relative to player */
export const HOLE_CARD_OFFSETS: CardOffsets = {
    holeCard1: {x: -40, y: -100},
    holeCard2: {x: 0, y: -100},
    holeCardAngle: 7,  // Degrees
};

// Hero cockpit BBS values → src/config/cockpit.ts

// =============================================================================
// CHIP DIMENSIONS
// =============================================================================

/** Chip dimensions (at 1920x1080 base) */
export const CHIP_SIZE: Size = {width: 40, height: 42};

/** Chip stack gap (visible edge height) */
export const CHIP_STACK_GAP = 4;

// =============================================================================
// TIMER RING
// =============================================================================

/** Timer ring configuration */
export const TIMER_RING = {
    /** Radius (at 1920x1080 base) */
    radius: 50,

    /** Line width */
    lineWidth: 6,

    /** Colors by time remaining */
    colors: {
        normal: 0x00FF00,    // Green (> 60%)
        warning: 0xFFFF00,   // Yellow (40-60%)
        danger: 0xFF8800,    // Orange (20-40%)
        critical: 0xFF0000,  // Red (< 20%)
    },
};

// =============================================================================
// ACTION BUTTONS
// =============================================================================

/** GamePad (action buttons) configuration */
export const GAMEPAD = {
    /** Position (normalized) */
    position: {x: 0.50, y: 0.92},

    /** Button dimensions (at 1920x1080 base) */
    button: {width: 140, height: 60},

    /** Spacing between buttons */
    spacing: 10,

    /** Minimum touch target size */
    minTouchTarget: 44,
};

// =============================================================================
// HERO SEAT ROTATION
// =============================================================================

/**
 * Get the visual position index for a seat, rotated so hero is at position 0.
 *
 * When hero sits at seat N, all seats rotate so that seat N appears at the
 * bottom center (visual position 0). This is standard poker client UX.
 *
 * @param tableSize - Number of seats (2, 5, 6, or 9)
 * @param serverSeat - The actual seat number from the server (0-indexed)
 * @param heroSeat - The hero's seat number, or null if watching
 * @returns The visual position index to use for coordinate lookup
 *
 * @example
 * // 6-max table, hero at seat 5
 * getVisualPositionIndex(6, 5, 5) // → 0 (hero at bottom center)
 * getVisualPositionIndex(6, 0, 5) // → 1
 * getVisualPositionIndex(6, 1, 5) // → 2
 */
export function getVisualPositionIndex(
    tableSize: number,
    serverSeat: number,
    heroSeat: number | null
): number {
    // No rotation when watching (hero not seated)
    if (heroSeat === null) return serverSeat;

    // Rotate so heroSeat maps to position 0
    // Formula: (serverSeat - heroSeat + tableSize) % tableSize
    return (serverSeat - heroSeat + tableSize) % tableSize;
}

/**
 * Get seat position with hero rotation applied.
 *
 * @param tableSize - Table size (2, 5, 6, or 9)
 * @param serverSeat - Server seat number (0-indexed)
 * @param heroSeat - Hero's seat, or null if watching
 * @returns The position for the visual location
 */
export function getRotatedSeatPosition(
    tableSize: 2 | 5 | 6 | 9,
    serverSeat: number,
    heroSeat: number | null
): SeatPosition {
    const visualIndex = getVisualPositionIndex(tableSize, serverSeat, heroSeat);
    const positions = SEAT_POSITIONS[tableSize];
    const position = positions[visualIndex];

    if (!position) {
        throw new Error(`Invalid visual index ${visualIndex} for ${tableSize}-max table`);
    }

    // Return position coordinates with original seat number preserved
    return {seat: serverSeat, x: position.x, y: position.y};
}

/**
 * Get offset (bet, hole cards, etc.) with hero rotation applied.
 *
 * @param offsets - The offset array for the table size
 * @param tableSize - Table size
 * @param serverSeat - Server seat number
 * @param heroSeat - Hero's seat, or null if watching
 * @returns The offset for the rotated visual position
 */
export function getRotatedOffset(
    offsets: Point[],
    tableSize: number,
    serverSeat: number,
    heroSeat: number | null
): Point | undefined {
    const visualIndex = getVisualPositionIndex(tableSize, serverSeat, heroSeat);
    return offsets[visualIndex];
}

// =============================================================================
// HELPER FUNCTIONS
// =============================================================================

/** Convert normalized position to pixel position */
export function toPixels(normalized: Point, containerSize: Size): Point {
    return {
        x: normalized.x * containerSize.width,
        y: normalized.y * containerSize.height,
    };
}

/** Convert pixel position to normalized position */
export function toNormalized(pixels: Point, containerSize: Size): Point {
    return {
        x: pixels.x / containerSize.width,
        y: pixels.y / containerSize.height,
    };
}

/** Get seat position in pixels */
export function getSeatPixelPosition(
    tableSize: 2 | 5 | 6 | 9,
    seat: number,
    containerSize: Size
): Point {
    const positions = SEAT_POSITIONS[tableSize];
    const seatPos = positions[seat];
    if (!seatPos) {
        throw new Error(`Invalid seat ${seat} for ${tableSize}-max table`);
    }
    return toPixels(seatPos, containerSize);
}
